/** One failing request field, as RFC 9457 `invalid_params` reports it. */
export interface InvalidParam {
  name: string;
  reason: string;
}

export class ApiError extends Error {
  status: number;
  detail: string;
  type: string;
  /**
   * Per-field validation failures, when the server sent them.
   *
   * A schema rejection's `detail` is the constant "Request validation failed" —
   * useless on its own. The server already names every offending field and why
   * in `extensions.invalid_params`; dropping it here left operators staring at
   * a form with no indication of which input was wrong.
   */
  invalidParams?: InvalidParam[];
  /**
   * Seconds the server asked us to wait before retrying, when it said so: a
   * 429's `Retry-After` header, else the body's `retry_after_seconds`. The
   * login screen turns it into a countdown instead of a dead button.
   */
  retryAfterSeconds?: number;

  constructor(
    status: number,
    detail: string,
    type: string,
    invalidParams?: InvalidParam[],
    retryAfterSeconds?: number,
  ) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.type = type;
    this.invalidParams = invalidParams;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Turn a thrown error into something an operator can act on.
 *
 * For a schema rejection the top-level `detail` is the constant "Request
 * validation failed", so on its own it tells the reader nothing about which of
 * a dozen inputs was refused. When the server named the fields, list them.
 */
export function describeApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.invalidParams?.length) {
    const lines = err.invalidParams.map((p) => (p.name ? `• ${p.name} — ${p.reason}` : `• ${p.reason}`));
    return [err.detail, ...lines].join('\n');
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

interface ApiClientConfig {
  getToken: () => string | null;
  onUnauthorized: () => void;
  baseUrl?: string | undefined;
}

interface AuthCodeRequestResponse {
  object: 'auth_code_request';
  email: string;
  expires_in: number;
  /** One-time receipt the verify step echoes back; an older server omits it. */
  challenge?: string;
}

interface UserResponse {
  object: 'user';
  id: string;
  email: string;
  tenant_id: string;
  role: string;
  role_id?: string | null;
  capabilities?: CapabilityMap;
}

interface AuthTokenResponse {
  object: 'auth_token';
  token: string;
  user: UserResponse;
}

export interface ConversationResponse {
  object: 'conversation';
  id: string;
  title: string;
  last_message_preview: string | null;
  status: string;
  processing_status: 'idle' | 'processing';
  unread: boolean;
  /**
   * Creator Mode toggle. When true, this conversation's claims schedule
   * against the RW-repo warm-pool — the agent can author skills, sub-agents,
   * and slash commands; writes commit back to git on session end.
   */
  creator_mode: boolean;
  /**
   * Per-conversation skill selection. `null` = unset (the agent sees every
   * skill the user's role grants). An array = the narrowed set the user picked
   * in the composer's skill selector (intersected with grants server-side).
   */
  selected_skill_ids: string[] | null;
  /**
   * Whether the title was set manually and is locked against LLM auto-titling.
   * Once true, the agent never overwrites the title; only manual edits change it.
   */
  title_locked: boolean;
  user_id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
}

/** A skill the current user is entitled to (from GET /auth/me/skills). */
export interface MySkill {
  skill_id: string;
  name: string;
  description: string;
}

/** A row in the GET /skills listing (admin Skills view). */
export interface SkillListItem {
  id: string;
  name: string;
  description: string;
  fileName: string;
  content: string;
  frontmatter: Record<string, unknown>;
}

/** GET /skills/{id} — parsed frontmatter + rel_root for git ops. */
export interface SkillDetailResponse {
  id: string;
  name: string;
  description: string;
  fileName: string;
  frontmatter: Record<string, unknown>;
  /** Slash-delimited path relative to the env repo root, e.g. ".claude/skills/foo". */
  rel_root: string;
}

/** GET /skills/{id}/files row. */
export interface SkillFile {
  path: string;
  type: 'file' | 'dir';
  size?: number;
  /** Populated only when fetched with `ref` — additions vs the parent commit. */
  additions?: number;
  /** Populated only when fetched with `ref` — deletions vs the parent commit. */
  deletions?: number;
}

/** GET /skills/{id}/file response. */
export interface SkillFileContent {
  path: string;
  content: string;
  encoding: 'utf-8' | 'base64';
  mime: string;
  diff_against_parent?: string;
}

/** GET /skills/{id}/history row. */
export interface SkillCommit {
  hash: string;
  short_hash: string;
  author: string;
  date: string;
  subject: string;
  files_changed: number;
  additions: number;
  deletions: number;
}

export type SkillInvocationStatus = 'pending' | 'completed' | 'failed';

/** GET /skills/{id}/invocations row. */
export interface SkillInvocationListItem {
  object: 'skill_invocation';
  id: string;
  skill_id: string;
  conversation_id: string;
  conversation_title: string | null;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  status: SkillInvocationStatus;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
}

/** Verbatim SDK content block as captured in skill_invocations.trace. */
export interface SkillInvocationTraceBlock {
  type: 'tool_use' | 'tool_result' | 'text' | 'thinking' | string;
  id?: string;
  name?: string;
  tool_use_id?: string;
  parent_tool_use_id?: string | null;
  input?: unknown;
  content?: unknown;
  is_error?: boolean;
  text?: string;
  [extra: string]: unknown;
}

/** GET /invocations/{id} response (single row + full trace). */
export interface SkillInvocationDetail extends SkillInvocationListItem {
  message_id: string | null;
  input: unknown;
  output: unknown;
  error_text: string | null;
  trace: SkillInvocationTraceBlock[];
}

/** Cursor-paginated GET /skills/{id}/invocations response. */
export interface SkillInvocationListResponse {
  object: 'list';
  data: SkillInvocationListItem[];
  has_more: boolean;
}

export interface AgentPanelResponse {
  object: 'agent_panel';
  id: string;
  conversation_id: string;
  tab_id: string;
  title: string;
  components: Array<{ type: string; id: string; props: Record<string, unknown> }>;
  created_at: string;
  updated_at: string;
}

// ---- Skills Repository (/skills-repository) — uploaded skill bundles ----
// Distinct from the git-authored /skills* surface above: ids are skr_/skv_,
// bundles are uploaded as zips, versions are immutable, and a conversation
// binds one by putting its skr_ id into `selected_skill_ids`.

/** One indexed file of a version. `mode` is an octal STRING ("0644" | "0755") — parse base 8, never coerce to a number. */
export interface RepositorySkillFile {
  path: string;
  size: number;
  sha256: string;
  mode: '0644' | '0755';
}

/** `skills_repository.version` — immutable; `files` present on the version-detail and skill-detail responses. */
export interface RepositorySkillVersion {
  object: 'skills_repository.version';
  id: string;
  skill_id: string;
  version: number;
  sha256: string;
  size_bytes: number;
  file_count: number;
  manifest: Record<string, unknown>;
  summary: string;
  author_id: string | null;
  created_at: string;
  files?: RepositorySkillFile[];
}

/** `skills_repository.skill` — `files` is the current version's index (detail response). */
export interface RepositorySkill {
  object: 'skills_repository.skill';
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  current_version: RepositorySkillVersion | null;
  files?: RepositorySkillFile[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Stripe-style page; `starting_after` is the last item's id. */
export interface RepositorySkillPage<T> {
  object: 'list';
  data: T[];
  has_more: boolean;
}

export interface UpdateRepositorySkillBody {
  description?: string | null;
  /** Rollback / roll-forward: point `current_version` at any version of the skill. */
  current_version_id?: string;
}

/** Raw bytes of one indexed file plus the headers the server stamps on them. */
export interface RepositorySkillFileBytes {
  bytes: Uint8Array;
  content_type: string;
  version_id: string;
  sha256: string;
}

interface ListResponse<T> {
  object: 'list';
  data: T[];
  has_more: boolean;
  url: string;
}

export interface MessageResponse {
  object: 'message';
  id: string;
  role: 'human' | 'ai';
  content: string;
  sdk_events?: Record<string, unknown>[];
  created_at?: string | undefined;
}

export type ApiKeyAudience = 'direct_api' | 'mcp' | 'openai_completions' | 'integration';

/** What identity a key acts as. */
export type ApiKeyIdentityMode = 'self' | 'agent' | 'role';
export type ApiKeyActsAs = 'user' | 'agent' | 'role';

export interface ApiKeyResponse {
  object: 'api_key';
  id: string;
  name: string | null;
  audience: ApiKeyAudience;
  key_prefix: string;
  key_hint: string;
  scopes: string[];
  acts_as: ApiKeyActsAs;
  agent_id: string | null;
  role_id: string | null;
  skill_ids: string[] | null;
  metadata?: Record<string, unknown>;
  revoked_at: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  /** Owning tenant — present only on `scope=subtree` listings. */
  tenant?: { id: string; name: string; slug: string; path: string };
}

export interface CreateApiKeyResponse extends ApiKeyResponse {
  /** The plaintext secret. Returned exactly once at mint time. Empty when custom_auth. */
  secret: string;
  /** True when the key authenticates by a creator-supplied custom token (secret is empty). */
  custom_auth?: boolean;
}

/** Audience-specific per-key sub-options (extensible). */
export interface ApiKeyCompletionsOptions {
  /** True when this key targets VAPI voice (gates the assistant-id binding + per-call 403 check). */
  vapi_mode?: boolean;
  vapi_session_mapping?: boolean;
  /** Required only in VAPI mode: every /chat/completions call verifies body.assistant.id against it. */
  vapi_assistant_id?: string;
  /** Per-key opt-out of the filler/gate LLM (default on). */
  filler_enabled?: boolean;
  custom_auth?: boolean;
}
export interface ApiKeyMetadata {
  completions?: ApiKeyCompletionsOptions;
  [k: string]: unknown;
}

export interface CreateApiKeyBody {
  name: string;
  audience: ApiKeyAudience;
  scopes?: string[];
  expires_at?: string;
  /** Identity binding. Defaults to 'self'. 'agent' needs agent_id; 'role' needs role_id. */
  identity_mode?: ApiKeyIdentityMode;
  agent_id?: string;
  role_id?: string;
  /** Optional per-key skill narrowing (self/role). Must be a subset of your own accessible skills. */
  skill_ids?: string[];
  /** Extensible per-key sub-options (audience-specific). */
  metadata?: ApiKeyMetadata;
  /** Write-only: the creator's own bearer token (openai_completions; the exact value VAPI sends). */
  custom_auth_secret?: string;
}

/**
 * PATCH /api-keys/{id} — partial update. Omitted fields stay unchanged; explicit
 * `null` clears (expires_at, skill_ids). Audience, prefix/secret, and the identity
 * binding are immutable — mint a new key to change them.
 */
export interface UpdateApiKeyBody {
  name?: string;
  scopes?: string[];
  /** ISO datetime; explicit null removes the expiry. */
  expires_at?: string | null;
  /** Explicit null removes the per-key skill narrowing. */
  skill_ids?: string[] | null;
  /** Replaces the whole metadata object when present. */
  metadata?: ApiKeyMetadata;
}

export interface PlanResponse {
  object: 'plan';
  id: string;
  task_id: string | null;
  conversation_id: string | null;
  tenant_id: string;
  version: number;
  status: 'draft' | 'pending_approval' | 'approved' | 'executing' | 'completed' | 'revised' | 'denied';
  steps: unknown[];
  data_access: unknown[];
  approved_by: string | null;
  denial_reason: string | null;
  metadata: Record<string, unknown>;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface TaskResponse {
  object: 'task';
  id: string;
  title: string;
  description: string | null;
  stage: string;
  overlay_state: string | null;
  conversation_id: string;
  assigned_to: string | null;
  metadata: Record<string, unknown>;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface FileResponse {
  object: 'file';
  id: string;
  filename: string;
  content_type: string;
  size: number;
  conversation_id: string;
  tenant_id: string;
  created_at: string;
}


/**
 * A vendor credential template — a reviewed credential shape, minus the secrets.
 *
 * Note what is NOT here: any field VALUE. A template names `client_secret` and
 * says it is a secret; the operator supplies it. That is what lets the catalog be
 * readable by everyone who can create a credential.
 */

/**
 * `status` is absent on purpose: a new template is always a draft, and the
 * server strips the key if sent. Declaring it here would advertise a no-op —
 * send `status: 'published'`, get a 201, receive a draft. Publishing is a
 * separate PATCH, because it is the act that asserts a human read the
 * destination list.
 */

/**
 * PATCH body. Unlike create, `status` IS accepted here — publishing and
 * unpublishing are updates, and the server re-validates every destination at
 * the draft→published transition. `slug` is immutable and so not offered.
 */



export interface SkillAccessRow {
  skill_id: string;
  name: string;
  description: string;
  granted: boolean;
  /** True when the grant references a skill that no longer exists on disk. */
  stale: boolean;
}

export type CapabilityMap = {
  full_access?: boolean;
  manage_users?: boolean;
  manage_roles?: boolean;
  manage_agents?: boolean;
  creator_mode?: boolean;
  sftp_access?: boolean;
};

export interface RoleResponse {
  object: 'role';
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  is_system: boolean;
  capabilities: CapabilityMap;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface TenantUserResponse {
  object: 'user';
  id: string;
  email: string;
  display_name: string | null;
  role_id: string | null;
  role_slug: string | null;
  role_capabilities: CapabilityMap;
  tenant_id: string;
  deactivated_at: string | null;
  deactivated_by: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// ---- SSH keys + SFTP (operator SFTP onto tenant workspaces; runbook docs/runbooks/sftp-access.md) ----

export interface UserSshKeyResponse {
  object: 'user_ssh_key';
  id: string;
  user_id: string;
  name: string;
  key_type: string;
  /** "SHA256:<base64>" — identical to `ssh-keygen -lf -E sha256`. */
  fingerprint: string;
  created_by: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
}

export interface CreateUserSshKeyBody {
  name: string;
  /** One line, OpenSSH format ("ssh-ed25519 AAAA… comment"); the comment is discarded. */
  public_key: string;
}

export interface SftpSettingsBody {
  enabled: boolean;
  allowed_cidrs: string[];
  allow_any: boolean;
}

export interface SftpSettingsResponse extends SftpSettingsBody {
  object: 'sftp_settings';
  updated_at: string | null;
  updated_by: string | null;
}

export type SftpAccessReason = 'no_capability' | 'sftp_disabled' | 'no_keys';

export interface SftpConnectionResponse {
  object: 'sftp_connection';
  host: string | null;
  port: number;
  username: string | null;
  host_key_fingerprint: string | null;
  server: {
    status: 'online' | 'offline' | 'unknown';
    version: string | null;
    last_heartbeat_at: string | null;
    active_sessions: number;
  };
  access: {
    permitted: boolean;
    reason: SftpAccessReason | null;
    visible_tenant_count: number;
  };
  command: string | null;
}

export interface TenantResponse {
  object: 'tenant';
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  path: string;
  depth: number;
  /** Host-system mapping, e.g. `st:tenant:3381249` — what an operator recognises. */
  external_id?: string | null;
  git_branch: string | null;
  settings: Record<string, unknown>;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface GitBindingResponse {
  object: 'git_binding';
  id: string;
  tenant_id: string;
  name: string;
  is_primary: boolean;
  repo_url: string;
  branch: string;
  repo_provider: string;
  git_credential_id: string | null;
  status: string;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateGitBindingBody {
  name?: string;
  is_primary?: boolean;
  repo_url: string;
  branch?: string;
  repo_provider?: 'generic' | 'github';
  git_credential_id?: string | null;
  metadata?: Record<string, unknown>;
}

export interface UpdateGitBindingBody {
  name?: string;
  is_primary?: boolean;
  repo_url?: string;
  branch?: string;
  repo_provider?: 'generic' | 'github';
  git_credential_id?: string | null;
  status?: 'active' | 'disabled';
  metadata?: Record<string, unknown>;
}

export type GitCredentialProvider = 'github' | 'gitlab' | 'bitbucket' | 'azure_devops' | 'generic';

/** `needs_reentry` is a credential migrated across without usable material. */
export type GitCredentialStatus = 'active' | 'revoked' | 'needs_reentry';

export interface GitCredentialResponse {
  object: 'git_credential';
  id: string;
  name: string;
  provider: GitCredentialProvider;
  auth_type: 'pat' | 'basic' | 'ssh' | 'github_app';
  username: string;
  host_pattern: string | null;
  status: GitCredentialStatus;
  /** Proof the material is there, without disclosing it. Never the token. */
  fingerprint: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  /** Repository ids currently pointing at this credential; blocks delete. */
  in_use_by: string[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CreateGitCredentialBody {
  name: string;
  /** Write-only. Sent here and on rotate; no response ever carries it back. */
  token: string;
  provider?: GitCredentialProvider;
  username?: string;
  host_pattern?: string;
  expires_at?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateGitCredentialBody {
  name?: string;
  /** Supplying a token rotates in place — the id and every repository using it
   *  are untouched, which is why rotation is an update and not a re-create. */
  token?: string;
  provider?: GitCredentialProvider;
  username?: string;
  host_pattern?: string | null;
  status?: 'active' | 'revoked';
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
}

export interface GitCredentialTestResponse {
  object: 'git_credential.test';
  id: string;
  repo_url: string;
  reachable: boolean;
  result: string;
  detail: string | null;
  checked_at: string;
}




/**
 * The mirror image of destination binding: instead of putting a secret we
 * already hold into a request, this takes a secret the vendor HANDS us out of
 * the response and swaps in a meaningless token, so the value never reaches
 * the cell. Revealed only back to the host it was captured from.
 */



export interface ChangelogEntryResponse {
  summary: string;
  category: string;
  tiers: string[];
}

export interface ChangelogReleaseResponse {
  object: 'changelog_release';
  id: string;
  date: string;
  stream: string;
  version: string | null;
  packages: Record<string, string> | null;
  entries: ChangelogEntryResponse[];
}

// --- Agents admin ----------------------------------------------------------

export interface AgentSummaryResponse {
  object: 'agent';
  id: string;
  slug: string;
  name: string;
  description: string | null;
  role: { id: string; slug: string; name: string };
  has_agent_md: boolean;
  has_soul_md: boolean;
  skill_count: number;
  status: 'active' | 'deactivated';
  created_at: string;
  updated_at: string;
}

export interface AgentDetailResponse extends AgentSummaryResponse {
  frontmatter: Record<string, unknown>;
  role_capabilities: Record<string, boolean>;
  granted_skills: Array<{ id: string; name: string }>;
  created_by_email: string;
}

export interface CreateAgentBody {
  slug: string;
  name: string;
  description?: string;
  role_id: string;
  include_soul?: boolean;
}

export interface UpdateAgentBody {
  name?: string;
  description?: string;
  role_id?: string;
}

// Sandbox Runtime Logs (GET /sandbox_runtimes*, cell-log/1 wire schema)
export type SandboxRunStatus = 'running' | 'completed' | 'error' | 'incomplete';

export interface SandboxRuntimeResponse {
  object: 'sandbox_runtime';
  pod_session_id: string;
  tenant_id: string;
  tenant_external_id: string | null;
  user_id: string | null;
  user_external_id: string | null;
  user_email: string | null;
  conversation_id: string | null;
  conversation_external_id: string | null;
  agent_type: string | null;
  sandbox_name: string | null;
  claim_name: string | null;
  live: boolean;
  lease_expires_at: string | null;
  last_status: SandboxRunStatus | null;
  first_started_at: string | null;
  last_activity_at: string | null;
  duration_ms: number | null;
  runs_count: number;
}

export interface SandboxRunResponse {
  object: 'sandbox_run';
  id: string;
  job_id: string;
  trace_id: string | null;
  status: SandboxRunStatus;
  error_message: string | null;
  sandbox_name: string | null;
  claim_name: string | null;
  leased: boolean;
  sandbox_image: string | null;
  pool_name: string | null;
  client_sdk_version: string | null;
  worker_sdk_version: string | null;
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
}

export interface SandboxRuntimeListResponse {
  object: 'list';
  data: SandboxRuntimeResponse[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface SandboxRuntimeDetailResponse extends SandboxRuntimeResponse {
  runs: SandboxRunResponse[];
}

export interface SandboxRuntimeListParams {
  limit?: number;
  starting_after?: string;
  search?: string;
  /** 'subtree' = effective tenant + all descendants (the operator view). */
  scope?: 'tenant' | 'subtree';
}

/** One normalized trace span (agent-server proxy of the SigNoz query API). */
export interface TraceSpanReference {
  trace_id: string;
  span_id: string;
  ref_type: string;
}

export interface TraceSpanResponse {
  span_id: string;
  trace_id?: string;
  parent_span_id: string | null;
  name: string;
  service: string;
  kind?: string;
  span_kind?: string;
  start_ns: number;
  duration_ns: number;
  status: string;
  status_code_string?: string;
  status_message?: string | null;
  has_error?: boolean;
  attributes: Record<string, string>;
  events?: unknown[];
  references?: TraceSpanReference[];
}

export interface SandboxRuntimeTraceResponse {
  object: 'trace';
  trace_id: string;
  spans: TraceSpanResponse[];
  /** Deep link into the SigNoz UI when the deployment has one configured. */
  signoz_url: string | null;
}

/** One cell-log/1 NDJSON record (or a heartbeat ping carrying `event`). */
export interface SandboxLogRecord {
  cell_log?: number;
  type?: 'line' | 'gap' | string;
  session_id?: string;
  org_id?: string;
  user_id?: string;
  turn_id?: string;
  stream?: string;
  level?: string;
  seq?: number;
  ts?: number;
  line?: string;
  truncated?: boolean;
  dropped?: number;
  js_seq?: number;
  event?: string;
}

export interface SandboxLogStreamParams {
  follow?: boolean;
  tail?: number;
  since?: string;
  cursor?: number;
  /** Reverse page: records strictly before this js_seq (scroll-up history). */
  before?: number;
  grep?: string;
  stream?: 'stdout' | 'stderr' | 'logger' | 'tap';
  level?: 'debug' | 'info' | 'warning' | 'error';
  turn?: string;
}

function randomHex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

function generateTraceparent(): string {
  return `00-${randomHex(16)}-${randomHex(8)}-01`;
}

// ── Proxy Vault ────────────────────────────────────────────────────────────
//
// No shape in this block carries a VALUE. Binding takes a raw string and
// answers with a ledger row; there is no response type here that could hold
// material, which is the console half of the same invariant the server keeps.

export interface ProxyVaultConnection {
  object: 'proxy_vault.connection';
  id: string;
  /** Immutable identifier — unique per organization, and what `export` emits. */
  name: string;
  /** Friendly label. Null means fall back to `name`. Edited in the YAML. */
  display_name: string | null;
  description: string | null;
  docs_url: string | null;
  kind: 'Connection' | 'Connector';
  tenant_id: string;
  tenant_name: string | null;
  tenant_external_id: string | null;
  status: 'incomplete' | 'active' | 'revoked';
  visibility: 'private' | 'children';
  materialization: 'aliased' | 'cleartext';
  spec_version: number;
  unbound: string[];
  captures: string[];
  hosts: string[];
  env_names: string[];
  bound_organizations: number;
  /** Of `bound_organizations`, how many are descendants of the connection's own organization. */
  bound_descendants: number;
  visible_organizations: number;
  created_at: string;
  updated_at: string;
  revoked_at: string | null;
  warnings?: string[];
}

export interface ProxyVaultLedgerEntry {
  name: string;
  kind: 'secret' | 'key' | 'certificate' | 'capture';
  state: 'bound' | 'generated' | 'runtime' | 'unbound';
  version: number | null;
  fingerprint: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  tenant_external_id: string | null;
  visibility: 'private' | 'children' | null;
  stand_in: string | null;
  bound_at: string | null;
  rotated_at: string | null;
  inherited: boolean;
  descendant: boolean;
  overridden_by: string | null;
}

export interface ProxyVaultRevision {
  version: number;
  author_id: string | null;
  summary: string;
  security_relevant: boolean;
  widened_paths: string[];
  created_at: string;
}

export interface ProxyVaultPreview {
  object: 'proxy_vault.preview';
  diff: string;
  widened: boolean;
  widened_paths: string[];
  summary: string;
  errors: Array<{ path: string; message: string }>;
  warnings: Array<{ path: string; message: string }>;
  not_yet_enabled: Array<{ path: string; message: string }>;
  conflicts: Array<{ reason: string; detail: string }>;
}

export interface ProxyVaultExchange {
  id: string;
  tenant_id: string;
  user_id: string | null;
  claim_id: string | null;
  connection_id: string | null;
  kind: 'http' | 'binary' | 'tcp';
  method: string | null;
  host: string;
  path: string | null;
  status: number | null;
  duration_ms: number | null;
  content_type: string | null;
  bytes_up: number | null;
  bytes_down: number | null;
  operations: Array<{ verb: string; target: string; where: string }>;
  verdict: 'forwarded' | 'refused';
  error_code: string | null;
  trace_id: string | null;
  truncated: boolean;
  created_at: string;
}

export interface ProxyVaultExchangeDetail extends ProxyVaultExchange {
  object: 'proxy_vault.exchange';
  request_headers: Record<string, unknown>;
  request_body: string | null;
  response_headers: Record<string, unknown>;
  response_body: string | null;
}

export interface ProxyVaultOverview {
  object: 'proxy_vault.overview';
  connections: { active: number; incomplete: number; revoked: number; organizations: number };
  last_24h: {
    exchanges: number;
    passthrough: number;
    reveals: number;
    redactions: number;
    refused: number;
    tcp_lanes: number;
  };
  custody: { secrets_held: number };
  top_destinations: Array<{ host: string; exchanges: number }>;
}

export interface ProxyVaultKey {
  name: string;
  /** Read off the pasted key, never declared in the document. */
  algorithm: 'RS256' | 'ES256' | null;
  /** THEIRS — the key that means something at the vendor. */
  fingerprint: string | null;
  /** OURS — what the cell signs with, and what the vendor would reject. */
  proxy_key_fingerprint: string | null;
  mount_at: string | null;
  tenant_id: string;
  visibility: 'private' | 'children';
  bound_at: string | null;
  rotated_at: string | null;
}

export interface ProxyVaultCapture {
  id: string;
  name: string;
  using_tenant_id: string;
  stand_in: string;
  /** The VENDOR's deadline, read off its token. Never ours to extend. */
  expires_at: string | null;
  expiry_source: 'token_exp' | 'declared' | 'platform_default' | null;
  created_at: string;
  last_used_at: string | null;
}

export interface ProxyVaultResolution {
  object: 'proxy_vault.resolution';
  env: string;
  outcome: 'resolved' | 'no_connection' | 'no_value';
  detail: string;
  connection: { id: string; name: string; tenant_id: string; inherited: boolean } | null;
  value: { id: string; tenant_id: string; inherited: boolean; visibility: string } | null;
  shadowed: Array<{ id: string; name: string; tenant_id: string }>;
}

// Sandbox Fleet (0050) — demand-driven cell pools + the registry store.
export interface SandboxPoolResponse {
  object: 'sandbox_pool';
  id: string;
  bundle_ref: string;
  bundle_key: string;
  agent_type: string;
  template_name: string;
  pool_name: string;
  replicas: number;
  ready_replicas: number | null;
  active: number;
  idle: number;
  status: 'provisioning' | 'ready' | 'retired' | 'deleting';
  image_digest: string | null;
  worker_sdk_version: string | null;
  worker_sdk_incompatible: boolean;
  registry_id: string | null;
  first_ready_at: string | null;
  last_claimed_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface SandboxRegistryResponse {
  object: 'sandbox_registry';
  id: string;
  name: string;
  login_server: string;
  auth_type: string;
  username: string | null;
  status: string;
  fingerprint: string | null;
  pull_secret_name: string;
  pools_referencing: number;
  last_used_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SandboxFleetSettingsResponse {
  object: 'sandbox_fleet_settings';
  default_replicas: number;
  retention_keep: number;
  updated_at: string;
}

// SDK governance (0051).
export interface SdkCatalogResponse {
  object: 'sdk_catalog';
  kinds: Record<
    string,
    {
      latest_version: string | null;
      min_supported_version: string | null;
      max_supported_version: string | null;
      releases: Array<{
        id: string;
        version: string;
        status: string;
        downloadable: boolean;
        notes: string | null;
        released_at: string | null;
      }>;
    }
  >;
}

export interface SdkObservationResponse {
  object: 'sdk_observation';
  id: string;
  tenant_id: string;
  kind: string;
  version: string;
  source: string;
  first_seen_at: string;
  last_seen_at: string;
  seen_count: number;
}

export interface SdkAlertResponse {
  object: 'sdk_alert';
  id: string;
  tenant_id: string;
  kind: string;
  version: string;
  severity: 'warning' | 'critical';
  sent_to: string;
  sent_at: string;
  notes: string | null;
}

export class ApiClient {
  private config: ApiClientConfig;
  // Effective tenant to act within (a descendant of the user's home tenant).
  // null = home tenant. Sent as X-Tenant-Context on every request when set.
  private tenantContext: string | null = null;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  /** Act within a descendant tenant (or null to reset to the home tenant). */
  setTenantContext(tenantId: string | null): void {
    this.tenantContext = tenantId;
  }

  getTenantContext(): string | null {
    return this.tenantContext;
  }

  private _url(path: string): string {
    return this.config.baseUrl ? `${this.config.baseUrl.replace(/\/$/, '')}${path}` : path;
  }

  private async _fetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.config.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Only send X-Tenant-Context when the caller hasn't set it explicitly per-request.
    if (this.tenantContext && !headers['X-Tenant-Context']) {
      headers['X-Tenant-Context'] = this.tenantContext;
    }

    // A multipart body must carry the boundary the browser generates; the preset
    // JSON content type would make the server read an empty form.
    if (options.body instanceof FormData) delete headers['Content-Type'];

    const response = await fetch(this._url(url), { ...options, headers });

    // A 401 from the two login endpoints is a refused code, not an expired
    // session: dropping the token there only bounced the sign-in screen.
    if (response.status === 401 && url !== '/auth/request_code' && url !== '/auth/verify_code') {
      this.config.onUnauthorized();
    }

    if (!response.ok) {
      let detail = `Request failed with status ${response.status}`;
      let type = 'unknown_error';
      let invalidParams: InvalidParam[] | undefined;
      // `Retry-After` as integer seconds (the HTTP-date form is ignored), else
      // the body's `retry_after_seconds`. Optional access: a bare response
      // double carries no `headers`.
      const retryAfter = response.headers?.get('Retry-After')?.trim();
      let retryAfterSeconds = /^\d+$/.test(retryAfter ?? '') ? Number(retryAfter) : undefined;
      try {
        const body = (await response.clone().json()) as {
          detail?: string;
          type?: string;
          invalid_params?: unknown;
          retry_after_seconds?: unknown;
        };
        if (body.detail) detail = body.detail;
        if (body.type) type = body.type;
        if (retryAfterSeconds === undefined && typeof body.retry_after_seconds === 'number') {
          retryAfterSeconds = body.retry_after_seconds;
        }
        // Defensive: this is an error path, and throwing while building an
        // error would replace a useful message with a confusing one.
        if (Array.isArray(body.invalid_params)) {
          const parsed = body.invalid_params
            .filter((p): p is { name?: unknown; reason?: unknown } => typeof p === 'object' && p !== null)
            .map((p) => ({ name: String(p.name ?? ''), reason: String(p.reason ?? '') }))
            .filter((p) => p.name || p.reason);
          if (parsed.length > 0) invalidParams = parsed;
        }
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(response.status, detail, type, invalidParams, retryAfterSeconds);
    }

    return response;
  }

  // Auth endpoints
  async requestCode(email: string): Promise<AuthCodeRequestResponse> {
    const response = await this._fetch('/auth/request_code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response.json() as Promise<AuthCodeRequestResponse>;
  }

  async verifyCode(email: string, code: string, challenge?: string): Promise<AuthTokenResponse> {
    const response = await this._fetch('/auth/verify_code', {
      method: 'POST',
      // JSON.stringify drops an undefined `challenge`, so an older server never
      // sees the key.
      body: JSON.stringify({ email, code, challenge }),
    });
    return response.json() as Promise<AuthTokenResponse>;
  }

  async getMe(): Promise<UserResponse> {
    const response = await this._fetch('/auth/me');
    return response.json() as Promise<UserResponse>;
  }

  async tokenExchange(body: Record<string, unknown>): Promise<AuthTokenResponse> {
    const response = await this._fetch('/auth/token_exchange', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<AuthTokenResponse>;
  }

  // Conversation endpoints
  async createConversation(body: Record<string, unknown>): Promise<ConversationResponse> {
    const response = await this._fetch('/conversations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<ConversationResponse>;
  }

  async listConversations(query?: Record<string, string>): Promise<ListResponse<ConversationResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/conversations${params}`);
    return response.json() as Promise<ListResponse<ConversationResponse>>;
  }

  async getConversation(id: string): Promise<ConversationResponse> {
    const response = await this._fetch(`/conversations/${id}`);
    return response.json() as Promise<ConversationResponse>;
  }

  async updateConversation(id: string, body: Record<string, unknown>): Promise<ConversationResponse> {
    const response = await this._fetch(`/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<ConversationResponse>;
  }

  async listAgentPanels(conversationId: string): Promise<AgentPanelResponse[]> {
    const response = await this._fetch(`/conversations/${conversationId}/panels`);
    const list = (await response.json()) as ListResponse<AgentPanelResponse>;
    return list.data;
  }

  async markConversationRead(id: string): Promise<ConversationResponse> {
    const response = await this._fetch(`/conversations/${id}/mark_read`, {
      method: 'POST',
    });
    return response.json() as Promise<ConversationResponse>;
  }

  async sendMessageStream(
    conversationId: string,
    content: string,
    options?: { signal?: AbortSignal; file_ids?: string[]; stream_events?: string[] },
  ): Promise<Response> {
    const token = this.config.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      traceparent: generateTraceparent(),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const body: Record<string, unknown> = { content };
    if (options?.file_ids && options.file_ids.length > 0) {
      body.file_ids = options.file_ids;
    }
    if (options?.stream_events && options.stream_events.length > 0) {
      body.stream_events = options.stream_events;
    }
    // POST /conversations/{id}/messages on agent-server runs the chat path.
    // Internally it loads the default profile (AGENT_SERVER_PROFILE_ID), calls
    // submitJob, persists user + assistant messages + sdk_events, and streams
    // NDJSON back here. See
    // packages/agent-server/src/features/chat/conversation-messages.service.ts.
    const response = await fetch(this._url(`/conversations/${conversationId}/messages`), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      ...(options?.signal ? { signal: options.signal } : {}),
    });
    if (response.status === 401) {
      this.config.onUnauthorized();
    }
    return response;
  }

  /** Paged sandbox runtime sessions (recent first) for the scoped tenant. */
  async listSandboxRuntimes(
    params: SandboxRuntimeListParams = {},
  ): Promise<SandboxRuntimeListResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.starting_after) search.set('starting_after', params.starting_after);
    if (params.search) search.set('search', params.search);
    if (params.scope) search.set('scope', params.scope);
    const qs = search.toString();
    const response = await this._fetch(`/sandbox_runtimes${qs ? `?${qs}` : ''}`);
    return response.json() as Promise<SandboxRuntimeListResponse>;
  }

  /**
   * One session's detail: aggregate + per-turn runs (trace ids included).
   * `tenantId` (from the list row) acts-as that descendant tenant — required
   * for sessions surfaced by scope=subtree that live in a child tenant.
   */
  async getSandboxRuntime(
    podSessionId: string,
    opts: { tenantId?: string } = {},
  ): Promise<SandboxRuntimeDetailResponse> {
    const response = await this._fetch(`/sandbox_runtimes/${encodeURIComponent(podSessionId)}`, {
      headers: opts.tenantId ? { 'X-Tenant-Context': opts.tenantId } : undefined,
    });
    return response.json() as Promise<SandboxRuntimeDetailResponse>;
  }

  /** Normalized trace spans for one of the session's runs (SigNoz proxy). */
  async getSandboxRuntimeTrace(
    podSessionId: string,
    traceId: string,
    opts: { tenantId?: string } = {},
  ): Promise<SandboxRuntimeTraceResponse> {
    const response = await this._fetch(
      `/sandbox_runtimes/${encodeURIComponent(podSessionId)}/traces/${encodeURIComponent(traceId)}`,
      { headers: opts.tenantId ? { 'X-Tenant-Context': opts.tenantId } : undefined },
    );
    return response.json() as Promise<SandboxRuntimeTraceResponse>;
  }

  /**
   * Open a cell-log/1 NDJSON stream for one sandbox runtime session. Mirrors
   * sendMessageStream: a raw fetch (the ApiClient JSON wrapper would consume
   * the body), caller parses via parseNdjsonStream and aborts via signal.
   */
  async streamSandboxRuntimeLogs(
    podSessionId: string,
    params: SandboxLogStreamParams = {},
    options?: { signal?: AbortSignal; tenantId?: string },
  ): Promise<Response> {
    const token = this.config.getToken();
    const headers: Record<string, string> = {
      Accept: 'application/x-ndjson',
      traceparent: generateTraceparent(),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Per-call tenant (child session via scope=subtree) wins over the global context.
    const tenantContext = options?.tenantId ?? this.tenantContext;
    if (tenantContext) {
      headers['X-Tenant-Context'] = tenantContext;
    }
    const search = new URLSearchParams();
    if (params.follow) search.set('follow', 'true');
    if (params.tail !== undefined) search.set('tail', String(params.tail));
    if (params.since) search.set('since', params.since);
    if (params.cursor !== undefined) search.set('cursor', String(params.cursor));
    if (params.before !== undefined) search.set('before', String(params.before));
    if (params.grep) search.set('grep', params.grep);
    if (params.stream) search.set('stream', params.stream);
    if (params.level) search.set('level', params.level);
    if (params.turn) search.set('turn', params.turn);
    const qs = search.toString();
    const response = await fetch(
      this._url(`/sandbox_runtimes/${encodeURIComponent(podSessionId)}/logs${qs ? `?${qs}` : ''}`),
      { headers, ...(options?.signal ? { signal: options.signal } : {}) },
    );
    if (response.status === 401) {
      this.config.onUnauthorized();
    }
    return response;
  }

  // ── Sandbox Fleet (0050) ──────────────────────────────────────────────────

  async listSandboxPools(
    params: { status?: string; limit?: number; starting_after?: string } = {},
  ): Promise<{ object: 'list'; data: SandboxPoolResponse[]; has_more: boolean; next_cursor: string | null }> {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.limit) search.set('limit', String(params.limit));
    if (params.starting_after) search.set('starting_after', params.starting_after);
    const qs = search.toString();
    const response = await this._fetch(`/sandbox_pools${qs ? `?${qs}` : ''}`);
    return response.json();
  }

  async prewarmSandboxPool(body: { image: string; agent_type: string; tenant_id?: string; replicas?: number }): Promise<SandboxPoolResponse> {
    const response = await this._fetch('/sandbox_pools', { method: 'POST', body: JSON.stringify(body) });
    return response.json();
  }

  async getSandboxPool(id: string): Promise<SandboxPoolResponse> {
    const response = await this._fetch(`/sandbox_pools/${encodeURIComponent(id)}`);
    return response.json();
  }

  async patchSandboxPool(
    id: string,
    body: { replicas?: number; status?: 'retired' | 'ready' },
  ): Promise<SandboxPoolResponse> {
    const response = await this._fetch(`/sandbox_pools/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json();
  }

  async deleteSandboxPool(id: string, opts: { force?: boolean } = {}): Promise<void> {
    await this._fetch(`/sandbox_pools/${encodeURIComponent(id)}${opts.force ? '?force=true' : ''}`, {
      method: 'DELETE',
    });
  }

  async listSandboxRegistries(): Promise<{ object: 'list'; data: SandboxRegistryResponse[] }> {
    const response = await this._fetch('/sandbox_registries');
    return response.json();
  }

  async createSandboxRegistry(body: {
    name: string;
    login_server: string;
    auth_type: 'token' | 'username_password' | 'cluster_identity';
    username?: string;
    /** Required for token/username_password; cluster_identity is secretless. */
    secret?: string;
  }): Promise<SandboxRegistryResponse> {
    const response = await this._fetch('/sandbox_registries', { method: 'POST', body: JSON.stringify(body) });
    return response.json();
  }

  async updateSandboxRegistry(
    id: string,
    body: { secret?: string; username?: string; status?: 'active' | 'disabled' },
  ): Promise<SandboxRegistryResponse> {
    const response = await this._fetch(`/sandbox_registries/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json();
  }

  async deleteSandboxRegistry(id: string): Promise<void> {
    await this._fetch(`/sandbox_registries/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async getSandboxFleetSettings(): Promise<SandboxFleetSettingsResponse> {
    const response = await this._fetch('/sandbox_fleet/settings');
    return response.json();
  }

  async putSandboxFleetSettings(body: {
    default_replicas?: number;
    retention_keep?: number;
  }): Promise<SandboxFleetSettingsResponse> {
    const response = await this._fetch('/sandbox_fleet/settings', { method: 'PUT', body: JSON.stringify(body) });
    return response.json();
  }

  // ── SDK governance (0051) ─────────────────────────────────────────────────

  async getSdkCatalog(): Promise<SdkCatalogResponse> {
    const response = await this._fetch('/sdk_releases');
    return response.json();
  }

  /** Authenticated artifact download → a Blob the caller turns into a save. */
  async downloadSdkArtifact(kind: string, version: string): Promise<{ blob: Blob; filename: string }> {
    const response = await this._fetch(
      `/sdk_releases/${encodeURIComponent(kind)}/${encodeURIComponent(version)}/download`,
    );
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await response.blob(), filename: match?.[1] ?? `${kind}-${version}` };
  }

  async listSdkUsage(
    params: { kind?: string; limit?: number } = {},
  ): Promise<{
    object: 'list';
    data: SdkObservationResponse[];
    stats: Record<string, { versions: number; tenants: number; total: number }>;
  }> {
    const search = new URLSearchParams();
    if (params.kind) search.set('kind', params.kind);
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    const response = await this._fetch(`/sdk_usage${qs ? `?${qs}` : ''}`);
    return response.json();
  }

  async listSdkAlerts(params: { limit?: number } = {}): Promise<{ object: 'list'; data: SdkAlertResponse[] }> {
    const search = new URLSearchParams();
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    const response = await this._fetch(`/sdk_alerts${qs ? `?${qs}` : ''}`);
    return response.json();
  }

  async createTask(body: Record<string, unknown>): Promise<TaskResponse> {
    const response = await this._fetch('/tasks', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TaskResponse>;
  }

  async listTasks(query?: Record<string, string>): Promise<ListResponse<TaskResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/tasks${params}`);
    return response.json() as Promise<ListResponse<TaskResponse>>;
  }

  async getTask(id: string): Promise<TaskResponse> {
    const response = await this._fetch(`/tasks/${id}`);
    return response.json() as Promise<TaskResponse>;
  }

  async updateTask(id: string, body: Record<string, unknown>): Promise<TaskResponse> {
    const response = await this._fetch(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TaskResponse>;
  }

  async transitionTask(id: string, body: Record<string, unknown>): Promise<TaskResponse> {
    const response = await this._fetch(`/tasks/${id}/transitions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TaskResponse>;
  }

  async listTaskPlans(taskId: string): Promise<ListResponse<PlanResponse>> {
    const response = await this._fetch(`/tasks/${taskId}/plans`);
    return response.json() as Promise<ListResponse<PlanResponse>>;
  }

  async listPlans(query?: Record<string, string>): Promise<ListResponse<PlanResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/plans${params}`);
    return response.json() as Promise<ListResponse<PlanResponse>>;
  }

  async uploadFile(conversationId: string, file: File): Promise<FileResponse> {
    const token = this.config.getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(this._url(`/conversations/${conversationId}/files`), {
      method: 'POST',
      headers,
      body: formData,
    });
    if (response.status === 401) {
      this.config.onUnauthorized();
    }
    if (!response.ok) {
      let detail = `Upload failed with status ${response.status}`;
      let type = 'upload_error';
      try {
        const body = (await response.clone().json()) as { detail?: string; type?: string };
        if (body.detail) detail = body.detail;
        if (body.type) type = body.type;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(response.status, detail, type);
    }
    return response.json() as Promise<FileResponse>;
  }

  async listFiles(conversationId: string): Promise<ListResponse<FileResponse>> {
    const response = await this._fetch(`/conversations/${conversationId}/files`);
    return response.json() as Promise<ListResponse<FileResponse>>;
  }

  async getFile(conversationId: string, fileId: string): Promise<FileResponse> {
    const response = await this._fetch(`/conversations/${conversationId}/files/${fileId}`);
    return response.json() as Promise<FileResponse>;
  }

  async listMessages(
    conversationId: string,
    query?: Record<string, string>,
  ): Promise<ListResponse<MessageResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/conversations/${conversationId}/messages${params}`);
    return response.json() as Promise<ListResponse<MessageResponse>>;
  }



  /**
   * `opts.tenantId` targets a DESCENDANT organisation, per call.
   *
   * Per call and never `setTenantContext`: a session-wide switch would silently
   * follow the operator onto every other screen. The server validates the target
   * is inside the caller's subtree and 403s otherwise, so this widens nothing —
   * it only lets one request say which organisation it is for.
   */

  /** `opts.tenantId` acts as the row's (possibly descendant) organisation. */

  /** `opts.tenantId` acts as the row's (possibly descendant) organisation. */

  // ── Vendor credential templates ───────────────────────────────────────────
  //
  // Reads are open to anyone who may create a credential — a template carries no
  // field VALUES, so the catalog holds nothing worth stealing. Writes are
  // super-admin only, and the server refuses them from an API key outright, so
  // the mutating calls below only ever succeed from a browser session.







  /** `opts.tenantId` acts as the row's (possibly descendant) organisation. */








  async listUsers(query?: Record<string, string>): Promise<ListResponse<TenantUserResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/users${params}`);
    return response.json() as Promise<ListResponse<TenantUserResponse>>;
  }

  async createUser(body: {
    email: string;
    role_id: string;
    display_name?: string | undefined;
  }): Promise<TenantUserResponse> {
    const response = await this._fetch('/users', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TenantUserResponse>;
  }

  async updateUser(
    id: string,
    body: { role_id?: string | undefined; display_name?: string | undefined },
  ): Promise<TenantUserResponse> {
    const response = await this._fetch(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TenantUserResponse>;
  }

  async deactivateUser(id: string): Promise<TenantUserResponse> {
    const response = await this._fetch(`/users/${id}/deactivate`, {
      method: 'POST',
    });
    return response.json() as Promise<TenantUserResponse>;
  }

  // ---- SSH keys (self, or an admin over the user's organization tree) ----
  async listUserSshKeys(userId: string): Promise<ListResponse<UserSshKeyResponse>> {
    const response = await this._fetch(`/users/${userId}/ssh_keys`);
    return response.json() as Promise<ListResponse<UserSshKeyResponse>>;
  }

  async addUserSshKey(userId: string, body: CreateUserSshKeyBody): Promise<UserSshKeyResponse> {
    const response = await this._fetch(`/users/${userId}/ssh_keys`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<UserSshKeyResponse>;
  }

  async deleteUserSshKey(userId: string, keyId: string): Promise<UserSshKeyResponse> {
    const response = await this._fetch(`/users/${userId}/ssh_keys/${keyId}`, { method: 'DELETE' });
    return response.json() as Promise<UserSshKeyResponse>;
  }

  // ---- SFTP (settings: full_access; connection: sftp_access | manage_users | full_access) ----
  async getSftpSettings(): Promise<SftpSettingsResponse> {
    const response = await this._fetch('/sftp/settings');
    return response.json() as Promise<SftpSettingsResponse>;
  }

  async putSftpSettings(body: SftpSettingsBody): Promise<SftpSettingsResponse> {
    const response = await this._fetch('/sftp/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<SftpSettingsResponse>;
  }

  async getSftpConnection(): Promise<SftpConnectionResponse> {
    const response = await this._fetch('/sftp/connection');
    return response.json() as Promise<SftpConnectionResponse>;
  }

  // ---- Roles (capability RBAC) ----
  async listRoles(): Promise<ListResponse<RoleResponse>> {
    const response = await this._fetch('/roles');
    return response.json() as Promise<ListResponse<RoleResponse>>;
  }

  async createRole(body: {
    name: string;
    slug: string;
    description?: string | undefined;
    capabilities: CapabilityMap;
  }): Promise<RoleResponse> {
    const response = await this._fetch('/roles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<RoleResponse>;
  }

  async updateRole(
    id: string,
    body: { name?: string; description?: string; capabilities?: CapabilityMap },
  ): Promise<RoleResponse> {
    const response = await this._fetch(`/roles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<RoleResponse>;
  }

  async deleteRole(id: string): Promise<void> {
    await this._fetch(`/roles/${id}`, { method: 'DELETE' });
  }

  // ---- Skills Repository (/skills-repository) ----

  async listRepositorySkills(
    params: { limit?: number; starting_after?: string } = {},
  ): Promise<RepositorySkillPage<RepositorySkill>> {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.starting_after) query.set('starting_after', params.starting_after);
    const qs = query.toString();
    const response = await this._fetch(`/skills-repository/skills${qs ? `?${qs}` : ''}`);
    return response.json() as Promise<RepositorySkillPage<RepositorySkill>>;
  }

  /** Multipart upload; the server validates the zip and answers 422 + `invalid_params` when it is refused. */
  async uploadRepositorySkill(bundle: File, summary?: string): Promise<RepositorySkill> {
    const form = new FormData();
    form.append('bundle', bundle);
    if (summary) form.append('summary', summary);
    const response = await this._fetch('/skills-repository/skills', { method: 'POST', body: form });
    return response.json() as Promise<RepositorySkill>;
  }

  async getRepositorySkill(id: string): Promise<RepositorySkill> {
    const response = await this._fetch(`/skills-repository/skills/${encodeURIComponent(id)}`);
    return response.json() as Promise<RepositorySkill>;
  }

  /** Publishes a new version (it becomes current); an identical bundle returns the existing version. */
  async uploadRepositorySkillVersion(id: string, bundle: File, summary?: string): Promise<RepositorySkillVersion> {
    const form = new FormData();
    form.append('bundle', bundle);
    if (summary) form.append('summary', summary);
    const response = await this._fetch(`/skills-repository/skills/${encodeURIComponent(id)}/versions`, {
      method: 'POST',
      body: form,
    });
    return response.json() as Promise<RepositorySkillVersion>;
  }

  async listRepositorySkillVersions(
    id: string,
    params: { limit?: number; starting_after?: string } = {},
  ): Promise<RepositorySkillPage<RepositorySkillVersion>> {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.starting_after) query.set('starting_after', params.starting_after);
    const qs = query.toString();
    const response = await this._fetch(
      `/skills-repository/skills/${encodeURIComponent(id)}/versions${qs ? `?${qs}` : ''}`,
    );
    return response.json() as Promise<RepositorySkillPage<RepositorySkillVersion>>;
  }

  async getRepositorySkillVersion(id: string, versionId: string): Promise<RepositorySkillVersion> {
    const response = await this._fetch(
      `/skills-repository/skills/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
    );
    return response.json() as Promise<RepositorySkillVersion>;
  }

  /** Raw bytes of one indexed file — current version unless `versionId` is given. */
  async getRepositorySkillFile(id: string, path: string, versionId?: string): Promise<RepositorySkillFileBytes> {
    const query = new URLSearchParams({ path });
    if (versionId) query.set('version', versionId);
    const response = await this._fetch(`/skills-repository/skills/${encodeURIComponent(id)}/file?${query.toString()}`);
    return {
      bytes: new Uint8Array(await response.arrayBuffer()),
      content_type: response.headers.get('Content-Type') ?? 'application/octet-stream',
      version_id: response.headers.get('X-Skills-Repository-Version') ?? '',
      sha256: response.headers.get('X-Content-Sha256') ?? '',
    };
  }

  async patchRepositorySkill(id: string, body: UpdateRepositorySkillBody): Promise<RepositorySkill> {
    const response = await this._fetch(`/skills-repository/skills/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<RepositorySkill>;
  }

  /** Soft delete — history is kept server-side; idempotent. */
  async deleteRepositorySkill(id: string): Promise<void> {
    await this._fetch(`/skills-repository/skills/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  // ---- Per-role skill access ----
  // GET returns the reconciled view: every live skill annotated with whether
  // this role has it granted, plus any stale grants (skill no longer on disk).
  async getRoleSkillAccess(roleId: string): Promise<ListResponse<SkillAccessRow>> {
    const response = await this._fetch(`/roles/${roleId}/skill-access`);
    return response.json() as Promise<ListResponse<SkillAccessRow>>;
  }

  // The skills the CURRENT signed-in user is entitled to (role grants ∩ on-disk,
  // or all on-disk for creator_mode). Drives the composer's skill selector —
  // any authenticated user may call it, and it never returns skills the user
  // isn't entitled to.
  async getMySkills(): Promise<MySkill[]> {
    const response = await this._fetch('/auth/me/skills');
    const list = (await response.json()) as ListResponse<MySkill>;
    return list.data;
  }

  // PUT replaces the role's grant set. Returns the fresh reconciled view.
  async setRoleSkillAccess(
    roleId: string,
    skillIds: string[],
  ): Promise<ListResponse<SkillAccessRow>> {
    const response = await this._fetch(`/roles/${roleId}/skill-access`, {
      method: 'PUT',
      body: JSON.stringify({ skill_ids: skillIds }),
    });
    return response.json() as Promise<ListResponse<SkillAccessRow>>;
  }

  async listSlashCommands(sessionId?: string): Promise<{ name: string; description: string }[]> {
    const params = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
    const response = await this._fetch(`/slash_commands${params}`);
    const body = (await response.json()) as { data?: { name: string; description: string }[] };
    return body.data ?? [];
  }

  async listChangelog(query?: Record<string, string>): Promise<ListResponse<ChangelogReleaseResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/changelog${params}`);
    return response.json() as Promise<ListResponse<ChangelogReleaseResponse>>;
  }

  async getPlan(planId: string): Promise<PlanResponse> {
    const response = await this._fetch(`/plans/${planId}`);
    return response.json() as Promise<PlanResponse>;
  }

  async approvePlan(
    planId: string,
  ): Promise<PlanResponse & { conversation_id?: string | null; trigger_ready?: boolean }> {
    const response = await this._fetch(`/plans/${planId}/approve`, { method: 'POST', body: '{}' });
    return response.json() as Promise<
      PlanResponse & { conversation_id?: string | null; trigger_ready?: boolean }
    >;
  }

  async denyPlan(planId: string, reason?: string): Promise<PlanResponse> {
    const response = await this._fetch(`/plans/${planId}/deny`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return response.json() as Promise<PlanResponse>;
  }

  async submitToolResponse(body: {
    tool_call_id: string;
    payload?: Record<string, unknown>;
  }): Promise<unknown> {
    const response = await this._fetch('/tool_responses', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json();
  }

  // ---------- API Keys ----------

  async listApiKeys(scope: 'mine' | 'subtree' = 'mine'): Promise<ApiKeyResponse[]> {
    // `subtree` (full_access only) lists every key in the caller's tenant
    // subtree with its owning tenant; the server answers 403 otherwise.
    const response = await this._fetch(scope === 'subtree' ? '/api-keys?scope=subtree' : '/api-keys');
    const data = (await response.json()) as { object: 'list'; data: ApiKeyResponse[] };
    return data.data;
  }

  async createApiKey(body: CreateApiKeyBody): Promise<CreateApiKeyResponse> {
    const response = await this._fetch('/api-keys', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<CreateApiKeyResponse>;
  }

  async updateApiKey(id: string, body: UpdateApiKeyBody): Promise<ApiKeyResponse> {
    const response = await this._fetch(`/api-keys/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<ApiKeyResponse>;
  }

  async revokeApiKey(id: string): Promise<ApiKeyResponse> {
    const response = await this._fetch(`/api-keys/${id}`, { method: 'DELETE' });
    return response.json() as Promise<ApiKeyResponse>;
  }

  // ---------- Skills admin (creator_mode | full_access only) ----------

  async listSkills(): Promise<SkillListItem[]> {
    const response = await this._fetch('/skills');
    const list = (await response.json()) as ListResponse<SkillListItem>;
    return list.data;
  }

  async getSkill(id: string): Promise<SkillDetailResponse> {
    const response = await this._fetch(`/skills/${encodeURIComponent(id)}`);
    return response.json() as Promise<SkillDetailResponse>;
  }

  async getSkillFiles(id: string, ref?: string): Promise<SkillFile[]> {
    const query = ref ? `?ref=${encodeURIComponent(ref)}` : '';
    const response = await this._fetch(`/skills/${encodeURIComponent(id)}/files${query}`);
    const list = (await response.json()) as ListResponse<SkillFile>;
    return list.data;
  }

  async getSkillFile(id: string, path: string, ref?: string): Promise<SkillFileContent> {
    const params = new URLSearchParams({ path });
    if (ref) params.set('ref', ref);
    const response = await this._fetch(`/skills/${encodeURIComponent(id)}/file?${params.toString()}`);
    return response.json() as Promise<SkillFileContent>;
  }

  async getSkillHistory(
    id: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<SkillCommit[]> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    const response = await this._fetch(
      `/skills/${encodeURIComponent(id)}/history${query ? `?${query}` : ''}`,
    );
    const list = (await response.json()) as ListResponse<SkillCommit>;
    return list.data;
  }

  async listSkillInvocations(
    skillId: string,
    options: { limit?: number; startingAfter?: string } = {},
  ): Promise<SkillInvocationListResponse> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.startingAfter) params.set('starting_after', options.startingAfter);
    const query = params.toString();
    const response = await this._fetch(
      `/skills/${encodeURIComponent(skillId)}/invocations${query ? `?${query}` : ''}`,
    );
    return response.json() as Promise<SkillInvocationListResponse>;
  }

  async getSkillInvocation(invocationId: string): Promise<SkillInvocationDetail> {
    const response = await this._fetch(`/invocations/${encodeURIComponent(invocationId)}`);
    return response.json() as Promise<SkillInvocationDetail>;
  }

  // ---------- Agents admin (manage_agents | creator_mode | full_access) ----------

  async listAgents(
    query: { status?: 'active' | 'deactivated' | 'all'; role_id?: string; search?: string } = {},
  ): Promise<AgentSummaryResponse[]> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.role_id) params.set('role_id', query.role_id);
    if (query.search) params.set('search', query.search);
    const qs = params.toString();
    const response = await this._fetch(`/agents${qs ? `?${qs}` : ''}`);
    const body = (await response.json()) as { object: 'list'; data: AgentSummaryResponse[] };
    return body.data;
  }

  async getAgent(agentId: string): Promise<AgentDetailResponse> {
    const response = await this._fetch(`/agents/${encodeURIComponent(agentId)}`);
    return response.json() as Promise<AgentDetailResponse>;
  }

  async createAgent(body: CreateAgentBody): Promise<AgentDetailResponse> {
    const response = await this._fetch('/agents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json() as Promise<AgentDetailResponse>;
  }

  async updateAgent(agentId: string, body: UpdateAgentBody): Promise<AgentDetailResponse> {
    const response = await this._fetch(`/agents/${encodeURIComponent(agentId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json() as Promise<AgentDetailResponse>;
  }

  async deactivateAgent(agentId: string): Promise<AgentDetailResponse> {
    const response = await this._fetch(`/agents/${encodeURIComponent(agentId)}/deactivate`, {
      method: 'POST',
    });
    return response.json() as Promise<AgentDetailResponse>;
  }

  async scaffoldAgentSoul(agentId: string): Promise<{ created: boolean; path: string }> {
    const response = await this._fetch(`/agents/${encodeURIComponent(agentId)}/soul`, {
      method: 'POST',
    });
    return response.json() as Promise<{ created: boolean; path: string }>;
  }

  // Generic git-backed reads — same shape as the skill endpoints, parameterized by resource.
  // (Skills already have first-class methods above; these are the agent variants.)

  async getAgentFiles(agentId: string, ref?: string): Promise<SkillFile[]> {
    const params = new URLSearchParams();
    if (ref) params.set('ref', ref);
    const qs = params.toString();
    const response = await this._fetch(
      `/agents/${encodeURIComponent(agentId)}/files${qs ? `?${qs}` : ''}`,
    );
    const body = (await response.json()) as { object: 'list'; data: SkillFile[] };
    return body.data;
  }

  async getAgentFile(agentId: string, path: string, ref?: string): Promise<SkillFileContent> {
    const params = new URLSearchParams({ path });
    if (ref) params.set('ref', ref);
    const response = await this._fetch(
      `/agents/${encodeURIComponent(agentId)}/file?${params.toString()}`,
    );
    return response.json() as Promise<SkillFileContent>;
  }

  async getAgentHistory(
    agentId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<SkillCommit[]> {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    const response = await this._fetch(
      `/agents/${encodeURIComponent(agentId)}/history${qs ? `?${qs}` : ''}`,
    );
    const body = (await response.json()) as { object: 'list'; data: SkillCommit[] };
    return body.data;
  }

  // Tenants (hierarchy) ------------------------------------------------------
  async listTenants(query?: Record<string, string>): Promise<ListResponse<TenantResponse>> {
    const params = query ? `?${new URLSearchParams(query).toString()}` : '';
    const response = await this._fetch(`/tenants${params}`);
    return (await response.json()) as ListResponse<TenantResponse>;
  }

  async getTenant(tenantId: string): Promise<TenantResponse> {
    const response = await this._fetch(`/tenants/${encodeURIComponent(tenantId)}`);
    return (await response.json()) as TenantResponse;
  }

  async createTenant(body: {
    name: string;
    slug: string;
    parent_id?: string | null;
    settings?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  }): Promise<TenantResponse> {
    const response = await this._fetch('/tenants', { method: 'POST', body: JSON.stringify(body) });
    return (await response.json()) as TenantResponse;
  }

  async updateTenant(
    tenantId: string,
    body: { name?: string; settings?: Record<string, unknown>; metadata?: Record<string, unknown> },
  ): Promise<TenantResponse> {
    const response = await this._fetch(`/tenants/${encodeURIComponent(tenantId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return (await response.json()) as TenantResponse;
  }

  async reparentTenant(tenantId: string, parentId: string): Promise<TenantResponse> {
    const response = await this._fetch(`/tenants/${encodeURIComponent(tenantId)}/reparent`, {
      method: 'POST',
      body: JSON.stringify({ parent_id: parentId }),
    });
    return (await response.json()) as TenantResponse;
  }

  async deleteTenant(tenantId: string): Promise<void> {
    await this._fetch(`/tenants/${encodeURIComponent(tenantId)}`, { method: 'DELETE' });
  }

  // Git bindings -------------------------------------------------------------
  async listGitBindings(tenantId: string): Promise<ListResponse<GitBindingResponse>> {
    const response = await this._fetch(`/tenants/${encodeURIComponent(tenantId)}/git-bindings`);
    return (await response.json()) as ListResponse<GitBindingResponse>;
  }

  async createGitBinding(tenantId: string, body: CreateGitBindingBody): Promise<GitBindingResponse> {
    const response = await this._fetch(`/tenants/${encodeURIComponent(tenantId)}/git-bindings`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return (await response.json()) as GitBindingResponse;
  }

  async updateGitBinding(
    tenantId: string,
    bindingId: string,
    body: UpdateGitBindingBody,
  ): Promise<GitBindingResponse> {
    const response = await this._fetch(
      `/tenants/${encodeURIComponent(tenantId)}/git-bindings/${encodeURIComponent(bindingId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    return (await response.json()) as GitBindingResponse;
  }

  async deleteGitBinding(tenantId: string, bindingId: string): Promise<void> {
    await this._fetch(
      `/tenants/${encodeURIComponent(tenantId)}/git-bindings/${encodeURIComponent(bindingId)}`,
      { method: 'DELETE' },
    );
  }

  // Git credentials ----------------------------------------------------------
  //
  // The tokens the repository registry clones WITH — a separate lane from the
  // Proxy Vault, because a clone is a server-side fetch that never traverses
  // the egress proxy. Owner-root scoped like the registry itself, so there is
  // no per-call tenant context here.
  //
  // No method returns a token. `token` goes UP on create and on rotate; every
  // response answers with a fingerprint instead.
  async listGitCredentials(): Promise<ListResponse<GitCredentialResponse>> {
    const response = await this._fetch('/git-credentials?limit=100');
    return (await response.json()) as ListResponse<GitCredentialResponse>;
  }

  async createGitCredential(body: CreateGitCredentialBody): Promise<GitCredentialResponse> {
    const response = await this._fetch('/git-credentials', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return (await response.json()) as GitCredentialResponse;
  }

  async updateGitCredential(
    id: string,
    body: UpdateGitCredentialBody,
  ): Promise<GitCredentialResponse> {
    const response = await this._fetch(`/git-credentials/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return (await response.json()) as GitCredentialResponse;
  }

  async deleteGitCredential(id: string): Promise<void> {
    await this._fetch(`/git-credentials/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async testGitCredential(id: string, repoUrl?: string): Promise<GitCredentialTestResponse> {
    const response = await this._fetch(`/git-credentials/${encodeURIComponent(id)}/test`, {
      method: 'POST',
      body: JSON.stringify(repoUrl ? { repo_url: repoUrl } : {}),
    });
    return (await response.json()) as GitCredentialTestResponse;
  }
  // ── Proxy Vault ──────────────────────────────────────────────────────────
  //
  // Every mutating call takes an optional `tenantId` and sends it as a
  // PER-CALL `X-Tenant-Context` header rather than through `setTenantContext`.
  // A session-wide switch would silently retarget every unrelated request the
  // console makes afterwards, which is how a write lands in the wrong
  // organization without anyone touching a picker.
  private _pvHeaders(tenantId?: string): Record<string, string> {
    return tenantId ? { 'X-Tenant-Context': tenantId } : {};
  }

  async listProxyVaultConnections(
    query?: Record<string, string>,
  ): Promise<ListResponse<ProxyVaultConnection>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/proxy-vault/connections${params}`);
    return response.json() as Promise<ListResponse<ProxyVaultConnection>>;
  }

  async getProxyVaultConnection(id: string): Promise<ProxyVaultConnection> {
    const response = await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}`);
    return response.json() as Promise<ProxyVaultConnection>;
  }

  /** Create from a YAML document. The body is the document itself, not JSON. */
  async createProxyVaultConnection(
    spec: string,
    opts: { tenantId?: string; asConnector?: boolean } = {},
  ): Promise<ProxyVaultConnection> {
    const path = opts.asConnector ? '/proxy-vault/connectors' : '/proxy-vault/connections';
    const response = await this._fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/yaml', ...this._pvHeaders(opts.tenantId) },
      body: spec,
    });
    return response.json() as Promise<ProxyVaultConnection>;
  }

  async updateProxyVaultConnection(
    id: string,
    spec: string,
    expectedVersion: number,
    tenantId?: string,
  ): Promise<ProxyVaultConnection> {
    const response = await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: this._pvHeaders(tenantId),
      body: JSON.stringify({ spec, expected_version: expectedVersion }),
    });
    return response.json() as Promise<ProxyVaultConnection>;
  }

  /** What Apply would do: the diff, the widening flag and any conflict. */
  async previewProxyVaultSpec(id: string, spec: string): Promise<ProxyVaultPreview> {
    const response = await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}/preview`, {
      method: 'POST',
      body: JSON.stringify({ spec }),
    });
    return response.json() as Promise<ProxyVaultPreview>;
  }

  async revokeProxyVaultConnection(
    id: string,
    reason: string | undefined,
    tenantId?: string,
  ): Promise<ProxyVaultConnection> {
    const response = await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
      headers: this._pvHeaders(tenantId),
      body: JSON.stringify(reason ? { reason } : {}),
    });
    return response.json() as Promise<ProxyVaultConnection>;
  }

  async deleteProxyVaultConnection(id: string, tenantId?: string): Promise<void> {
    await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: this._pvHeaders(tenantId),
    });
  }

  async exportProxyVaultConnection(id: string): Promise<string> {
    const response = await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}/export`);
    return response.text();
  }

  async listProxyVaultValues(
    id: string,
    tenantId?: string,
  ): Promise<ListResponse<ProxyVaultLedgerEntry>> {
    const response = await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}/values`, {
      headers: this._pvHeaders(tenantId),
    });
    return response.json() as Promise<ListResponse<ProxyVaultLedgerEntry>>;
  }

  /**
   * Bind a value. WRITE-ONLY — the answer is the ledger, never the material.
   * The body is the raw value so a PEM keeps its newlines and a secret with a
   * quote in it needs no escaping.
   */
  async bindProxyVaultValue(
    id: string,
    name: string,
    value: string,
    opts: { visibility?: 'private' | 'children'; tenantId?: string } = {},
  ): Promise<ListResponse<ProxyVaultLedgerEntry>> {
    const params = opts.visibility ? `?visibility=${opts.visibility}` : '';
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/values/${encodeURIComponent(name)}${params}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'text/plain', ...this._pvHeaders(opts.tenantId) },
        body: value,
      },
    );
    return response.json() as Promise<ListResponse<ProxyVaultLedgerEntry>>;
  }

  /**
   * Paste a private key in. The BODY is the PEM — no JSON envelope, no
   * multipart, no file picker anywhere in this product.
   *
   * Write-only like every other value, and more so: the answer is two
   * fingerprints, and no endpoint on the server will ever hand the key back.
   */
  async pasteProxyVaultKey(
    id: string,
    name: string,
    pem: string,
    opts: { visibility?: 'private' | 'children'; tenantId?: string } = {},
  ): Promise<ProxyVaultKey> {
    const params = opts.visibility ? `?visibility=${opts.visibility}` : '';
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/keys/${encodeURIComponent(name)}${params}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/x-pem-file', ...this._pvHeaders(opts.tenantId) },
        body: pem,
      },
    );
    return response.json() as Promise<ProxyVaultKey>;
  }

  async listProxyVaultKeys(id: string, tenantId?: string): Promise<ListResponse<ProxyVaultKey>> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/keys`,
      { headers: this._pvHeaders(tenantId) },
    );
    return response.json() as Promise<ListResponse<ProxyVaultKey>>;
  }

  /** Regenerate OUR key. Theirs is untouched, and the vendor is not involved. */
  async rotateProxyVaultProxyKey(
    id: string,
    name: string,
    tenantId?: string,
  ): Promise<{ proxy_key_fingerprint: string; retiring_until: string }> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/keys/${encodeURIComponent(name)}/rotate`,
      { method: 'POST', headers: this._pvHeaders(tenantId) },
    );
    return response.json() as Promise<{ proxy_key_fingerprint: string; retiring_until: string }>;
  }

  /**
   * Live vendor sessions. Stand-ins and deadlines only — there is no token in
   * this response and no endpoint that would produce one.
   */
  async listProxyVaultCaptures(
    id: string,
    tenantId?: string,
  ): Promise<ListResponse<ProxyVaultCapture>> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/captures`,
      { headers: this._pvHeaders(tenantId) },
    );
    return response.json() as Promise<ListResponse<ProxyVaultCapture>>;
  }

  async revokeProxyVaultCapture(id: string, captureId: string, tenantId?: string): Promise<void> {
    await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/captures/${encodeURIComponent(captureId)}`,
      { method: 'DELETE', headers: this._pvHeaders(tenantId) },
    );
  }

  async rotateProxyVaultValue(
    id: string,
    name: string,
    value: string,
    tenantId?: string,
  ): Promise<ListResponse<ProxyVaultLedgerEntry>> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/values/${encodeURIComponent(name)}/rotate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain', ...this._pvHeaders(tenantId) },
        body: value,
      },
    );
    return response.json() as Promise<ListResponse<ProxyVaultLedgerEntry>>;
  }

  async renameProxyVaultValue(
    id: string,
    name: string,
    to: string,
    tenantId?: string,
  ): Promise<ListResponse<ProxyVaultLedgerEntry>> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/values/${encodeURIComponent(name)}/rename`,
      { method: 'POST', headers: this._pvHeaders(tenantId), body: JSON.stringify({ to }) },
    );
    return response.json() as Promise<ListResponse<ProxyVaultLedgerEntry>>;
  }

  async setProxyVaultValueVisibility(
    id: string,
    name: string,
    visibility: 'private' | 'children',
    tenantId?: string,
  ): Promise<ListResponse<ProxyVaultLedgerEntry>> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/values/${encodeURIComponent(name)}`,
      { method: 'PATCH', headers: this._pvHeaders(tenantId), body: JSON.stringify({ visibility }) },
    );
    return response.json() as Promise<ListResponse<ProxyVaultLedgerEntry>>;
  }

  async deleteProxyVaultValue(id: string, name: string, tenantId?: string): Promise<void> {
    await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/values/${encodeURIComponent(name)}`,
      { method: 'DELETE', headers: this._pvHeaders(tenantId) },
    );
  }

  async explainProxyVaultResolution(
    id: string,
    env: string,
    tenantId?: string,
  ): Promise<ProxyVaultResolution> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/resolution?env=${encodeURIComponent(env)}`,
      { headers: this._pvHeaders(tenantId) },
    );
    return response.json() as Promise<ProxyVaultResolution>;
  }

  async listProxyVaultRevisions(id: string): Promise<ListResponse<ProxyVaultRevision>> {
    const response = await this._fetch(`/proxy-vault/connections/${encodeURIComponent(id)}/revisions`);
    return response.json() as Promise<ListResponse<ProxyVaultRevision>>;
  }

  async getProxyVaultRevision(id: string, version: number): Promise<{ version: number; spec: string }> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/revisions/${version}`,
    );
    return response.json() as Promise<{ version: number; spec: string }>;
  }

  async diffProxyVaultRevisions(
    id: string,
    from: number,
    to: number,
  ): Promise<{ diff: string; widened: boolean; widened_paths: string[] }> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/revisions/${from}/diff?to=${to}`,
    );
    return response.json() as Promise<{ diff: string; widened: boolean; widened_paths: string[] }>;
  }

  async restoreProxyVaultRevision(
    id: string,
    version: number,
    tenantId?: string,
  ): Promise<ProxyVaultConnection> {
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/revisions/${version}/restore`,
      { method: 'POST', headers: this._pvHeaders(tenantId) },
    );
    return response.json() as Promise<ProxyVaultConnection>;
  }

  async getProxyVaultOverview(): Promise<ProxyVaultOverview> {
    const response = await this._fetch('/proxy-vault/overview');
    return response.json() as Promise<ProxyVaultOverview>;
  }

  async listProxyVaultTraffic(
    query: Record<string, string> = {},
  ): Promise<ListResponse<ProxyVaultExchange> & { has_more: boolean }> {
    const params = Object.keys(query).length ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/proxy-vault/traffic${params}`);
    return response.json() as Promise<ListResponse<ProxyVaultExchange> & { has_more: boolean }>;
  }

  async listProxyVaultConnectionTraffic(
    id: string,
    query: Record<string, string> = {},
  ): Promise<ListResponse<ProxyVaultExchange> & { has_more: boolean }> {
    const params = Object.keys(query).length ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(
      `/proxy-vault/connections/${encodeURIComponent(id)}/traffic${params}`,
    );
    return response.json() as Promise<ListResponse<ProxyVaultExchange> & { has_more: boolean }>;
  }

  async getProxyVaultExchange(exchangeId: string): Promise<ProxyVaultExchangeDetail> {
    const response = await this._fetch(`/proxy-vault/traffic/${encodeURIComponent(exchangeId)}`);
    return response.json() as Promise<ProxyVaultExchangeDetail>;
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
